(() => {
  const KEY = 'carryfizz:wishlist:v1';
  const buttonSelector = '[data-cf-wishlist-button]';
  const openSelector = '[data-cf-wishlist-open]';
  const closeSelector = '[data-cf-wishlist-close]';
  let toastTimer;
  let previouslyFocused = null;

  const read = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  };

  const write = (items) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (_) {}
  };

  const getId = (button) => String(button?.dataset?.productId || '');

  const setButtonState = (button, active) => {
    if (!(button instanceof HTMLElement)) return;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute('title', active ? 'Remove from wishlist' : 'Save to wishlist');
    const productTitle = button.dataset.productTitle || 'this item';
    button.setAttribute(
      'aria-label',
      active ? `Remove ${productTitle} from wishlist` : `Save ${productTitle} to wishlist`
    );
  };

  const syncButtons = () => {
    const ids = new Set(read().map((item) => String(item.id)));
    document.querySelectorAll(buttonSelector).forEach((button) => {
      setButtonState(button, ids.has(getId(button)));
    });
  };

  const syncHeaderCount = () => {
    const count = read().length;
    document.querySelectorAll('[data-cf-wishlist-count]').forEach((badge) => {
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.hidden = count === 0;
    });
  };

  const placePdpButtons = (root = document) => {
    root.querySelectorAll('.cf-wishlist-button--pdp').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;

      // Quick Add already has a compact purchase flow; keep PDP-only wishlist UI out.
      if (button.closest('#quick-add-dialog, .quick-add-modal')) {
        button.remove();
        return;
      }

      const productInfo = button.closest('product-component') || root.querySelector('product-component');
      const details = productInfo?.querySelector('.product-details') || root.querySelector('.product-details');
      if (details && button.parentElement !== details) details.appendChild(button);
      button.classList.add('cf-wishlist-ready');
    });
  };

  const ensureToast = () => {
    let toast = document.querySelector('.cf-wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'cf-wishlist-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    return toast;
  };

  const showToast = (message) => {
    const toast = ensureToast();
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 1500);
  };

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character]);

  const normalizeVariantId = (value) => {
    const raw = String(value || '');
    return raw.includes('/') ? raw.split('/').pop() : raw;
  };

  const renderDrawer = () => {
    const drawer = document.querySelector('[data-cf-wishlist-drawer]');
    if (!drawer) return;

    const items = read();
    const list = drawer.querySelector('[data-cf-wishlist-items]');
    const empty = drawer.querySelector('[data-cf-wishlist-empty]');
    const summary = drawer.querySelector('[data-cf-wishlist-summary]');

    if (summary) summary.textContent = `${items.length} saved ${items.length === 1 ? 'item' : 'items'}`;
    if (empty) empty.hidden = items.length > 0;
    if (!list) return;

    if (items.length === 0) {
      list.innerHTML = '';
      return;
    }

    list.innerHTML = items.map((item) => {
      const id = escapeHtml(item.id);
      const title = escapeHtml(item.title || 'Saved item');
      const url = escapeHtml(item.url || (item.handle ? `/products/${item.handle}` : '#'));
      const image = escapeHtml(item.image || '');
      const price = escapeHtml(item.price || '');
      const variantId = normalizeVariantId(item.variantId);
      const variantsCount = Number(item.variantsCount || 0);
      const canQuickAdd = Boolean(variantId) && variantsCount <= 1 && item.available !== false;

      const action = canQuickAdd
        ? `<button type="button" class="cf-wishlist-item__primary" data-cf-wishlist-add data-variant-id="${escapeHtml(variantId)}" data-product-id="${id}">Add to Basket</button>`
        : `<a class="cf-wishlist-item__primary" href="${url}">Choose Options</a>`;

      return `
        <article class="cf-wishlist-item" data-cf-wishlist-item="${id}">
          <a class="cf-wishlist-item__image" href="${url}" aria-label="View ${title}">
            ${image ? `<img src="${image}" alt="" loading="lazy">` : ''}
          </a>
          <div class="cf-wishlist-item__details">
            <div class="cf-wishlist-item__top">
              <div>
                <a class="cf-wishlist-item__title" href="${url}">${title}</a>
                ${price ? `<div class="cf-wishlist-item__price">${price}</div>` : ''}
              </div>
              <button type="button" class="cf-wishlist-item__remove" data-cf-wishlist-remove="${id}" aria-label="Remove ${title} from wishlist">
                <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 4 16 16M16 4 4 16" fill="none" stroke="currentColor" stroke-width="1.15" stroke-linecap="round"/></svg>
              </button>
            </div>
            <div class="cf-wishlist-item__actions">
              ${action}
              <a class="cf-wishlist-item__secondary" href="${url}">View Product</a>
            </div>
          </div>
        </article>
      `;
    }).join('');
  };

  const openDrawer = () => {
    const drawer = document.querySelector('[data-cf-wishlist-drawer]');
    const panel = drawer?.querySelector('.cf-wishlist-drawer__panel');
    if (!drawer || !panel) return;

    previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    renderDrawer();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cf-wishlist-drawer-open');
    requestAnimationFrame(() => panel.focus({ preventScroll: true }));
  };

  const closeDrawer = () => {
    const drawer = document.querySelector('[data-cf-wishlist-drawer]');
    if (!drawer) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cf-wishlist-drawer-open');
    previouslyFocused?.focus?.({ preventScroll: true });
    previouslyFocused = null;
  };

  const syncAll = () => {
    syncButtons();
    syncHeaderCount();
    renderDrawer();
  };

  const toggle = (button) => {
    const id = getId(button);
    if (!id) return;

    const items = read();
    const index = items.findIndex((item) => String(item.id) === id);
    let added = false;

    if (index >= 0) {
      items.splice(index, 1);
    } else {
      items.unshift({
        id,
        handle: button.dataset.productHandle || '',
        title: button.dataset.productTitle || '',
        url: button.dataset.productUrl || '',
        image: button.dataset.productImage || '',
        price: button.dataset.productPrice || '',
        variantId: button.dataset.productVariantId || '',
        variantsCount: Number(button.dataset.productVariantsCount || 0),
        available: button.dataset.productAvailable !== 'false',
        savedAt: Date.now()
      });
      added = true;
    }

    write(items);
    syncAll();

    button.classList.remove('is-pulse');
    void button.offsetWidth;
    button.classList.add('is-pulse');
    setTimeout(() => button.classList.remove('is-pulse'), 260);

    showToast(added ? 'Saved to wishlist' : 'Removed from wishlist');
    window.dispatchEvent(new CustomEvent('cf:wishlist:change', { detail: { items } }));
  };

  const removeItem = (id) => {
    const items = read().filter((item) => String(item.id) !== String(id));
    write(items);
    syncAll();
    showToast('Removed from wishlist');
    window.dispatchEvent(new CustomEvent('cf:wishlist:change', { detail: { items } }));
  };

  const cartSummaryFromAjax = (cart) => ({
    id: cart?.token || '',
    totalQuantity: Number(cart?.item_count || 0),
    cost: {
      totalAmount: {
        amount: (Number(cart?.total_price || 0) / 100).toFixed(2),
        currencyCode: cart?.currency || 'USD'
      }
    },
    lines: Array.isArray(cart?.items) ? cart.items.map((line) => ({
      id: line?.key || String(line?.id || ''),
      quantity: Number(line?.quantity || 0),
      cost: {
        totalAmount: {
          amount: (Number(line?.final_line_price || 0) / 100).toFixed(2),
          currencyCode: cart?.currency || 'USD'
        }
      }
    })) : [],
    discountCodes: Array.isArray(cart?.discount_codes)
      ? cart.discount_codes.map((discount) => ({
          applicable: discount?.applicable !== false,
          code: discount?.code || ''
        }))
      : []
  });

  const addToCart = async (button) => {
    const variantId = normalizeVariantId(button.dataset.variantId);
    if (!variantId || button.disabled) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    const root = window.Shopify?.routes?.root || '/';
    const cartPromise = (async () => {
      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });

      if (!addResponse.ok) {
        const error = await addResponse.json().catch(() => ({}));
        throw new Error(error?.description || error?.message || 'Could not add item');
      }

      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error('Could not refresh basket');
      const cart = await cartResponse.json();

      return {
        cart: cartSummaryFromAjax(cart),
        detail: { source: 'wishlist' }
      };
    })();

    const updateEvent = new Event('shopify:cart:lines-update', { bubbles: true });
    updateEvent.action = 'add';
    updateEvent.context = 'standard-action';
    updateEvent.lines = [{ merchandiseId: variantId, quantity: 1 }];
    updateEvent.promise = cartPromise;
    closeDrawer();
    document.dispatchEvent(updateEvent);

    try {
      await cartPromise;
      button.textContent = 'Added';
      showToast('Added to basket');
      setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText;
      }, 1000);
    } catch (error) {
      button.disabled = false;
      button.textContent = originalText;
      showToast(error?.message || 'Could not add item');
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const wishlistButton = target.closest(buttonSelector);
    if (wishlistButton) {
      event.preventDefault();
      event.stopPropagation();
      toggle(wishlistButton);
      return;
    }

    if (target.closest(openSelector)) {
      event.preventDefault();
      openDrawer();
      return;
    }

    if (target.closest(closeSelector)) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const removeButton = target.closest('[data-cf-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      removeItem(removeButton.getAttribute('data-cf-wishlist-remove'));
      return;
    }

    const addButton = target.closest('[data-cf-wishlist-add]');
    if (addButton) {
      event.preventDefault();
      addToCart(addButton);
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.querySelector('[data-cf-wishlist-drawer].is-open')) {
      closeDrawer();
    }
  });

  document.addEventListener('shopify:product:select', (event) => {
    const variant = event?.variant || event?.detail?.variant;
    if (!variant?.id) return;
    document.querySelectorAll('.cf-wishlist-button--pdp').forEach((pdpButton) => {
      pdpButton.dataset.productVariantId = normalizeVariantId(variant.id);
      if (variant.price?.amount) {
        try {
          const currency = variant.price.currencyCode || 'USD';
          pdpButton.dataset.productPrice = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency
          }).format(Number(variant.price.amount));
        } catch (_) {}
      }
      pdpButton.dataset.productAvailable = variant.availableForSale === false ? 'false' : 'true';
    });
  });

  window.addEventListener('storage', (event) => {
    if (event.key === KEY) syncAll();
  });

  const init = (root = document) => {
    placePdpButtons(root);
    syncAll();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:reorder', () => init());
})();